// Trust Marketplace Frontend v1.0.5 - Enrollment Material UI Redesign (Nov 11, 2025)
// Desktop offline read-cache: MUST be first so it wraps axios.defaults.adapter
// before any service module creates its axios instance (instances created
// before the override keep the original adapter). No-op on web.
import './lib/offline/httpCache'
import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from '@/lib/queryClient'
import App from './app'
import './globals.css'
import './src/styles/members.css'
import './src/styles/design-system.css' // Design system utilities
import './src/styles/v2-theme.css' // v2 theme layer (scoped under .v2-theme class)
import './src/styles/triveny-platform.css' // Triveny.ai design system overlay (scoped under .triveny-platform)
import './src/styles/triveny-home.css' // Triveny.ai full-bleed home (scoped under .triveny-home)
import './src/i18n/config' // Initialize i18next
import { BUILD_VERSION, FEATURES } from './src/version'

// Translation Provider for multi-language support
import { TranslationProvider } from '@/contexts/TranslationContext'

// App Loader - shows loading UI while contexts initialize
import { AppLoader } from '@/components/AppLoader'

// Tooltip Provider for Radix UI tooltips
import { TooltipProvider } from '@/components/ui/tooltip'

// App-wide toast surface (sonner) — every toast entry point renders here.
import { Toaster } from '@/components/ui/sonner'

// Datadog RUM, Logs, Session Replay & Error Tracking (Primary Observability)
import { DatadogProvider } from './src/providers/DatadogProvider'

// One-shot reload on stale chunk references after a redeploy. This is REACTIVE:
// it fires only once a lazy chunk the old bundle asks for has already 404'd.
// UpdateAvailableBanner is the proactive half — it notices a new build before
// the user trips over it, and asks rather than reloading under them.
import { installChunkErrorHandler } from './lib/lazyWithRetry'

// Tells a long-open tab that a newer build has been deployed
import UpdateAvailableBanner from '@/components/system/UpdateAvailableBanner'

// Last line of defence. Without a boundary at the root, ANY uncaught render
// error unmounts the whole tree and leaves the user staring at a white page
// with no message and no way back — which is exactly what a super admin on
// Trust Care hit repeatedly (React #310, `e._result.default` on a failed lazy
// chunk, and two ReferenceErrors, all logged against the same account).
import { ErrorBoundary } from '@/components/ErrorBoundary'

installChunkErrorHandler()

// The app's React Query client (lib/queryClient — AuthContext empties it on sign-out)

// Member Portal Pages
import MemberPortalLayout from './src/app/member/layout'
import MemberHome from './src/app/member/home/page'
import MemberLogin from './src/app/member/login/page'
import MemberForgotPassword from './src/app/member/forgot-password/page'
import MemberRegister from './src/app/member/register/page'
import MemberJoin from './src/app/member/join/page'
import MemberDashboard from './src/app/member/dashboard/page'
import MemberInbox from './src/app/member/inbox/page'
import MemberReferrals from './src/app/member/referrals/page'
import MemberFindADoula from './src/app/member/find-a-doula/page'
import DoulaProfile from './src/app/doula/profile/page'
import DoulaCaseload from './src/app/doula/caseload/page'
import DoulaOffers from './src/app/doula/offers/page'
import DoulaPayments from './src/app/doula/payments/page'
import DoulaMember from './src/app/doula/member/page'
import DoulaRespond from './src/app/doula/respond/page'
import PublicCardEmbed from './src/app/public/card/page'
import MemberDisputes from './src/app/member/disputes/page'
import MemberDocuments from './src/app/member/documents/page'
import MemberNotes from './src/app/member/notes/page'
import MemberNotifications from './src/app/member/notifications/page'
import MemberSettings from './src/app/member/settings/page'
import MemberEnrollments from './src/app/member/enrollments/page'
import MemberIncentives from './src/app/member/incentives/page'
import MemberWorkCompliance from './src/app/member/work-compliance/page'
import MemberVerifiedHours from './src/app/member/verified-hours/page'
import VerifyCheckinPage from './components/verify/VerifyCheckinPage'
import MemberCoursesPage from './src/app/member/courses/page'
import CourseViewerPage from './src/app/member/courses/CourseViewerPage'
import MemberTrainingPage from './src/app/member/training/page'

// Staff/Admin Registration & Login
import RegisterPage from './src/app/register/page'
import StaffLoginPage from './src/app/login/page'
import AcceptInvitationPage from './src/app/accept-invitation/page'
import AcceptOrgInvitePage from './src/app/accept-org-invite/page'
import StaffForgotPasswordPage from './src/app/forgot-password/page'
import StaffResetPasswordPage from './src/app/reset-password/page'
import Setup2FAPage from './src/app/setup-2fa/page'
import OAuthSuccessPage from './src/app/auth/oauth-success/page'
import SignupPage from './src/app/signup/page'
import CraftBuilder, { CraftView } from './src/platform/modules/web-presence-v2/CraftBuilder'

// SAML SSO Callback
import SamlCallbackPage from './src/app/auth/saml-callback/page'

// Public Consent Pages
import TermsOfUse from './src/app/consent/terms-of-use/page'
import HipaaAuthorization from './src/app/consent/hipaa-authorization/page'
import PrivacyPolicy from './src/app/consent/privacy-policy/page'

// Admin Compliance Document Editor
import ComplianceDocumentEditor from './src/app/admin/compliance-documents/editor/page'

// QR Code Scan Handler
import QRScanPage from './src/app/qr/page'

// Public Shared Onboarding View (Option A — no auth)
import SharedOnboardingPage from './src/app/shared/onboarding/page'

// Customer Onboarding Portal (Option B — limited login)
import OnboardingPortalLoginPage from './src/app/onboarding-portal/login/page'
import AcceptOnboardingInvitePage from './src/app/onboarding-portal/accept-invite/page'
import OnboardingPortalPlansPage from './src/app/onboarding-portal/plans/page'
import OnboardingPortalPlanDetailPage from './src/app/onboarding-portal/plans/detail'

// Customer Portal Pages
import CustomerPortalLoginPage from './src/app/customer-portal/login/page'
import CustomerPortalRegisterPage from './src/app/customer-portal/register/page'
import CustomerPortalDashboardPage from './src/app/customer-portal/dashboard/page'
import CustomerPortalTicketsPage from './src/app/customer-portal/tickets/page'
import CustomerPortalNewTicketPage from './src/app/customer-portal/tickets/new/page'
import CustomerPortalTicketDetailPage from './src/app/customer-portal/tickets/[ticketId]/page'
import CustomerPortalKBPage from './src/app/customer-portal/knowledge-base/page'
import CustomerPortalKBArticlePage from './src/app/customer-portal/knowledge-base/[slug]/page'
import CustomerPortalProfilePage from './src/app/customer-portal/profile/page'

// Employer Portal Pages (Legacy - redirect to employee-portal)
import EmployerDashboard from './src/app/employer/dashboard/page'
import EmployerBenefits from './src/app/employer/benefits/page'
import EmployerNavigation from './src/app/employer/navigation/page'
import EmployerRecommendations from './src/app/employer/recommendations/page'
import EmployerLeave from './src/app/employer/leave/page'
import EmployerWellness from './src/app/employer/wellness/page'
import EmployerMessages from './src/app/employer/messages/page'
import EmployerSettings from './src/app/employer/settings/page'
import EmployerConsent from './src/app/employer/consent/page'

// Employee Benefits Portal Pages (New unified portal for employee-members)
import EmployeePortalDashboard from './src/app/employee-portal/dashboard/page'
import EmployeePortalBenefits from './src/app/employee-portal/benefits/page'
import EmployeePortalMemberServices from './src/app/employee-portal/member-services/page'
import EmployeePortalIncentives from './src/app/employee-portal/incentives/page'
import EmployeePortalMessages from './src/app/employee-portal/messages/page'
import EmployeePortalSettings from './src/app/employee-portal/settings/page'

// Public Marketing/Landing Pages (WeaveIn.ai)
import SolutionsEmployers from './src/app/solutions/employers/page'
import SolutionsHealthPlans from './src/app/solutions/health-plans/page'
import SolutionsProviders from './src/app/solutions/providers/page'
import SolutionsHubs from './src/app/solutions/hubs/page'
import SolutionsWorkforceManagement from './src/app/solutions/workforce-management/page'
import SolutionsEmergencyResponse from './src/app/solutions/emergency-response/page'
import CompanyAbout from './src/app/company/about/page'
import CompanyTeam from './src/app/company/team/page'
import ContactPage from './src/app/contact/page'
import ResourcesPage from './src/app/resources/page'
import ROICalculatorPage from './src/app/roi-calculator/page'
import ImpactPage from './src/app/impact/page'
import PlatformPage from './src/app/platform/page'
import FunderCoveragePage from './src/app/funder-coverage/page'
import DevelopersPage from './src/app/developers/page'
import SecurityPage from './src/app/security/page'
import PartnersPage from './src/app/partners/page'

// Staff v2 Redesign Pages
import DesignIndex from './components/v2/DesignIndex'

// Workforce Module Pages
import WorkforceDashboard from './src/app/workforce/dashboard/page'
import WorkforceCandidates from './src/app/workforce/candidates/page'
import WorkforceRoles from './src/app/workforce/roles/page'
import WorkforceCertifications from './src/app/workforce/certifications/page'
import WorkforceCurricula from './src/app/workforce/curricula/page'
import WorkforceMentoring from './src/app/workforce/mentoring/page'
import WorkforceRoster from './src/app/workforce/roster/page'
import WorkforceSkillsGap from './src/app/workforce/skills-gap/page'
// LmsCoursesAdminPage (courses list) and StaffLearnPage (my-learning list) are
// rendered only inside the app shell now (app.tsx tabs); their standalone routes
// redirect into the shell via WorkforceTabRedirect. Only the immersive per-course
// pages keep standalone routes here.
import CourseEditorPage from './src/app/workforce/courses/CourseEditorPage'
import CourseInsightsPage from './src/app/workforce/courses/CourseInsightsPage'
import CourseLearnerPage from './src/app/workforce/learn/CourseLearnerPage'

// TRUS-692: the public meeting guest page (/m/{token}) — no shell, no auth; loads on demand.
const GuestMeetingPage = React.lazy(() => import('./src/app/m/page'))

console.log(`Trust Marketplace v1.0.5 - Enrollment Material UI - Build: ${BUILD_VERSION}`)
console.log('Features enabled:', FEATURES)
console.log('Main.tsx loaded successfully')

// The workforce LMS "list" pages (curricula, classroom courses, my learning) are
// ALSO app-shell tabs (app.tsx renders them by activeTab, inside the sidebar).
// Historically they were double-mounted as standalone routes here too, which
// render WITHOUT the sidebar — so any nav that lands the URL on one of these
// paths (a "Back to Curricula" link, browser back out of an immersive course,
// or a direct URL) dropped the sidebar and stranded the user at full width
// (TRUS-286). Redirect those paths back into the shell on the right tab: set
// activeTab (App restores it from localStorage on mount) and Navigate to '/'.
// The immersive per-course routes (/workforce/courses/:courseId,
// /workforce/learn/:courseId) intentionally stay full-screen and are untouched.
function WorkforceTabRedirect({ tab }: { tab: string }) {
  try {
    localStorage.setItem('activeTab', tab)
  } catch {
    /* private mode — App falls back to its default tab */
  }
  return <Navigate to="/" replace />
}

// TRUS-733: member-portal invitation emails used to link to
// /member-register?token=…, which had no route. Registration lives at
// /member/register/:token; send already-emailed links there.
// Verify QR self check-in (/verify/checkin/:token): public, no session, outside the shell.
function VerifyCheckinRoute() {
  const { token = '' } = useParams()
  return <VerifyCheckinPage token={token} />
}

function LegacyMemberRegisterRedirect() {
  const token = new URLSearchParams(window.location.search).get('token')
  return <Navigate to={token ? `/member/register/${encodeURIComponent(token)}` : '/member/register'} replace />
}

const rootElement = document.getElementById('root')
console.log('Root element:', rootElement)

if (rootElement) {
  const root = ReactDOM.createRoot(rootElement)
  console.log('React root created')

  root.render(
    <React.StrictMode>
      <DatadogProvider>
        <QueryClientProvider client={queryClient}>
          <ErrorBoundary>
          <TranslationProvider>
            {/* One toaster for every route: App shell, its early-return pages
                (form preview, triveny home, /popout/*) and the portal routes below. */}
            <Toaster />
            <AppLoader>
            <TooltipProvider>
            <UpdateAvailableBanner />
            <BrowserRouter>
                <Routes>
                  {/* Staff/Admin Login & Registration */}
                  <Route path="/login" element={<StaffLoginPage />} />
                  <Route path="/forgot-password" element={<StaffForgotPasswordPage />} />
                  <Route path="/reset-password" element={<StaffResetPasswordPage />} />
                  <Route path="/setup-2fa" element={<Setup2FAPage />} />
                  <Route path="/auth/oauth-success" element={<OAuthSuccessPage />} />
                  <Route path="/signup" element={<SignupPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
                  <Route path="/accept-org-invite" element={<AcceptOrgInvitePage />} />

                  {/* Web Presence v2 — isolated Craft.js layered builder (Phase 1
                      scaffold). Standalone surface; does NOT touch the live Studio. */}
                  <Route path="/design/v2" element={<CraftBuilder />} />
                  <Route path="/design/v2/view" element={<CraftView />} />

                  {/* Doula match offer answered from an emailed link — no account
                      required, so it must stay outside the authenticated shell. */}
                  <Route path="/doula/respond" element={<DoulaRespond />} />

                  {/* A meeting guest link (TRUS-692): no account, no session — outside
                      the shell, and it never calls a staff or member API. */}
                  <Route path="/m/:token" element={<React.Suspense fallback={null}><GuestMeetingPage /></React.Suspense>} />

                  {/* Her card, rendered by us inside a partner's app (§3.12).
                      Authorised by a short-lived pass in the URL, so it has no
                      session and must stay outside the shell. The /public/
                      prefix is load-bearing: frame-ancestors is 'self'
                      everywhere else, and the component could not be framed. */}
                  <Route path="/public/card" element={<PublicCardEmbed />} />

                  {/* SAML SSO Callback */}
                  <Route path="/auth/saml-callback" element={<SamlCallbackPage />} />

                  {/* Member Portal Routes — public/auth pages outside the shell */}
                  <Route path="/member/home" element={<MemberHome />} />
                  {/* The member door (TRUS-778): /help is the short public address
                      for the same page; /member/join is self sign-up. */}
                  <Route path="/help" element={<MemberHome />} />
                  <Route path="/member/join" element={<MemberJoin />} />
                  <Route path="/member/login" element={<MemberLogin />} />
                  <Route path="/member/forgot-password" element={<MemberForgotPassword />} />
                  <Route path="/member/register" element={<MemberRegister />} />
                  <Route path="/member/register/:token" element={<MemberRegister />} />
                  <Route path="/member-register" element={<LegacyMemberRegisterRedirect />} />
                  {/* Authenticated member pages share the portal shell (topbar + paper bg) */}
                  <Route element={<MemberPortalLayout />}>
                    <Route path="/member/dashboard" element={<MemberDashboard />} />
                    <Route path="/member/inbox" element={<MemberInbox />} />
                    <Route path="/member/referrals" element={<MemberReferrals />} />
                    <Route path="/member/find-a-doula" element={<MemberFindADoula />} />
                    <Route path="/doula/profile" element={<DoulaProfile />} />
                    <Route path="/doula/caseload" element={<DoulaCaseload />} />
                    <Route path="/doula/offers" element={<DoulaOffers />} />
                    <Route path="/doula/payments" element={<DoulaPayments />} />
                    <Route path="/doula/member/:memberId" element={<DoulaMember />} />
                    <Route path="/member/disputes" element={<MemberDisputes />} />
                    <Route path="/member/documents" element={<MemberDocuments />} />
                    <Route path="/member/notes" element={<MemberNotes />} />
                    <Route path="/member/notifications" element={<MemberNotifications />} />
                    <Route path="/member/settings" element={<MemberSettings />} />
                    <Route path="/member/enrollments" element={<MemberEnrollments />} />
                    <Route path="/member/incentives" element={<MemberIncentives />} />
                    <Route path="/member/work-compliance" element={<MemberWorkCompliance />} />
                    <Route path="/member/verified-hours" element={<MemberVerifiedHours />} />
                    <Route path="/member/courses" element={<MemberCoursesPage />} />
                    {/* My Training shares the portal shell like every other authed
                        member page — so Back stays inside the portal (sidebar +
                        /member/dashboard) instead of dropping to the public
                        /member/home landing. */}
                    <Route path="/member/training" element={<MemberTrainingPage />} />
                  </Route>
                  {/* Course viewer stays immersive (no shell) */}
                  <Route path="/member/courses/:courseId" element={<CourseViewerPage />} />

                  {/* Public Consent Pages */}
                  <Route path="/consent/terms-of-use" element={<TermsOfUse />} />
                  <Route path="/consent/hipaa-authorization" element={<HipaaAuthorization />} />
                  <Route path="/consent/privacy-policy" element={<PrivacyPolicy />} />

                  {/* Admin Compliance Document Editor */}
                  <Route path="/admin/compliance-documents/editor" element={<ComplianceDocumentEditor />} />

                  {/* QR Code Scan Handler (Public) */}
                  <Route path="/qr/:token" element={<QRScanPage />} />

                  {/* Verify: a member checks in hours at a qualified site from its poster (no auth). */}
                  <Route path="/verify/checkin/:token" element={<VerifyCheckinRoute />} />

                  {/* Public Shared Onboarding View (no auth) */}
                  <Route path="/shared/onboarding/:token" element={<SharedOnboardingPage />} />

                  {/* Customer Onboarding Portal (limited login) */}
                  <Route path="/onboarding-portal/login" element={<OnboardingPortalLoginPage />} />
                  <Route path="/onboarding-portal/accept-invite" element={<AcceptOnboardingInvitePage />} />
                  <Route path="/onboarding-portal/plans" element={<OnboardingPortalPlansPage />} />
                  <Route path="/onboarding-portal/plans/:planId" element={<OnboardingPortalPlanDetailPage />} />

                  {/* Customer Support Portal */}
                  <Route path="/customer-portal/login" element={<CustomerPortalLoginPage />} />
                  <Route path="/customer-portal/register" element={<CustomerPortalRegisterPage />} />
                  <Route path="/customer-portal/dashboard" element={<CustomerPortalDashboardPage />} />
                  <Route path="/customer-portal" element={<CustomerPortalDashboardPage />} />
                  <Route path="/customer-portal/tickets" element={<CustomerPortalTicketsPage />} />
                  <Route path="/customer-portal/tickets/new" element={<CustomerPortalNewTicketPage />} />
                  <Route path="/customer-portal/tickets/:ticketId" element={<CustomerPortalTicketDetailPage />} />
                  <Route path="/customer-portal/knowledge-base" element={<CustomerPortalKBPage />} />
                  <Route path="/customer-portal/knowledge-base/:slug" element={<CustomerPortalKBArticlePage />} />
                  <Route path="/customer-portal/profile" element={<CustomerPortalProfilePage />} />

                  {/* Employee Benefits Portal Routes (Primary for employee-members) */}
                  <Route path="/employee-portal/dashboard" element={<EmployeePortalDashboard />} />
                  <Route path="/employee-portal/benefits" element={<EmployeePortalBenefits />} />
                  <Route path="/employee-portal/member-services" element={<EmployeePortalMemberServices />} />
                  <Route path="/employee-portal/incentives" element={<EmployeePortalIncentives />} />
                  <Route path="/employee-portal/messages" element={<EmployeePortalMessages />} />
                  <Route path="/employee-portal/settings" element={<EmployeePortalSettings />} />
                  <Route path="/employee-portal" element={<EmployeePortalDashboard />} />

                  {/* Legacy Employer Portal Routes (redirect to employee-portal) */}
                  <Route path="/employer/dashboard" element={<EmployerDashboard />} />
                  <Route path="/employer/consent" element={<EmployerConsent />} />
                  <Route path="/employer/benefits" element={<EmployerBenefits />} />
                  <Route path="/employer/navigation" element={<EmployerNavigation />} />
                  <Route path="/employer/recommendations" element={<EmployerRecommendations />} />
                  <Route path="/employer/leave" element={<EmployerLeave />} />
                  <Route path="/employer/wellness" element={<EmployerWellness />} />
                  <Route path="/employer/messages" element={<EmployerMessages />} />
                  <Route path="/employer/settings" element={<EmployerSettings />} />
                  <Route path="/employer" element={<EmployerDashboard />} />

                  {/* Public Marketing Pages (WeaveIn.ai) */}
                  <Route path="/solutions/employers" element={<SolutionsEmployers />} />
                  <Route path="/solutions/health-plans" element={<SolutionsHealthPlans />} />
                  <Route path="/solutions/providers" element={<SolutionsProviders />} />
                  <Route path="/solutions/hubs" element={<SolutionsHubs />} />
                  <Route path="/solutions/workforce-management" element={<SolutionsWorkforceManagement />} />
                  <Route path="/solutions/emergency-response" element={<SolutionsEmergencyResponse />} />
                  <Route path="/company/about" element={<CompanyAbout />} />
                  <Route path="/company/team" element={<CompanyTeam />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/resources" element={<ResourcesPage />} />
                  <Route path="/roi-calculator" element={<ROICalculatorPage />} />
                  <Route path="/platform" element={<PlatformPage />} />
                  <Route path="/impact" element={<ImpactPage />} />
                  <Route path="/funder-coverage" element={<FunderCoveragePage />} />
                  <Route path="/developers" element={<DevelopersPage />} />
                  <Route path="/security" element={<SecurityPage />} />
                  <Route path="/partners" element={<PartnersPage />} />
                  {/* Retired pages: their content moved, their links still arrive. */}
                  <Route path="/case-studies" element={<Navigate to="/impact" replace />} />
                  <Route path="/solutions/developers" element={<Navigate to="/developers" replace />} />
                  <Route path="/solutions/care-coordination" element={<Navigate to="/platform" replace />} />

                  {/* Workforce Module Routes */}
                  <Route path="/workforce/dashboard" element={<WorkforceDashboard />} />
                  <Route path="/workforce/candidates" element={<WorkforceCandidates />} />
                  <Route path="/workforce/candidates/:id" element={<WorkforceCandidates />} />
                  <Route path="/workforce/roles" element={<WorkforceRoles />} />
                  <Route path="/workforce/roles/:id" element={<WorkforceRoles />} />
                  <Route path="/workforce/certifications" element={<WorkforceCertifications />} />
                  <Route path="/workforce/curricula" element={<WorkforceTabRedirect tab="workforce-curricula" />} />
                  <Route path="/workforce/curricula/:id" element={<WorkforceCurricula />} />
                  <Route path="/workforce/mentoring" element={<WorkforceMentoring />} />
                  <Route path="/workforce/mentoring/:id" element={<WorkforceMentoring />} />
                  <Route path="/workforce/roster" element={<WorkforceRoster />} />
                  <Route path="/workforce/skills-gap" element={<WorkforceSkillsGap />} />
                  <Route path="/workforce/courses" element={<WorkforceTabRedirect tab="workforce-courses" />} />
                  <Route path="/workforce/courses/:courseId" element={<CourseEditorPage />} />
                  <Route path="/workforce/courses/:courseId/insights" element={<CourseInsightsPage />} />
                  <Route path="/workforce/learn" element={<WorkforceTabRedirect tab="workforce-learn" />} />
                  <Route path="/workforce/learn/:courseId" element={<CourseLearnerPage />} />
                  <Route path="/workforce" element={<WorkforceDashboard />} />

                  {/* Staff v2 Redesign */}
                  <Route path="/v2" element={<DesignIndex />} />

                  {/* Admin/Main App Routes */}
                  <Route path="/*" element={<App />} />
                  </Routes>
              </BrowserRouter>
            </TooltipProvider>
            </AppLoader>
          </TranslationProvider>
          </ErrorBoundary>
        </QueryClientProvider>
      </DatadogProvider>
    </React.StrictMode>,
  )
  console.log('App rendered')
} else {
  console.error('Root element not found!')
}

